import React, { useMemo, useRef, useState } from 'react';
import { Button, Modal, Form, InputNumber, Input, Select, DatePicker, Upload, message, Space, Typography, Tooltip } from 'antd';
import type { UploadProps } from 'antd';
import { PlusOutlined, FileExcelOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import Dashboard from '../../components/Templates/Dashboard';
import DataTable, { type DataTableRef } from '../../components/Charts/DataTable';
import type { ColumnsType } from 'antd/es/table';
import type { FieldConfig } from '../../components/ForgeQueryBuilder';
import {
  operatingDataApi,
  type ImportSelfPurchaseCostResult,
  type OperatingDataOverviewResponse,
  type OperatingDataStoreRow,
  type ManualDataRequest,
} from '../../services/statistics/operating-data';
import { useShopMap } from '../../services/store/organization.store';
import { OrderType } from '../../types/process/order';
import dayjs from 'dayjs';
import './OperatingDataOverview.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const PENDING_RENT_COLOR = '#fa8c16';
const LOSS_COLOR = '#f5222d';
const EXPECTED_REVENUE_COLOR = '#52c41a';
type WarehouseSourceFilter = 'platform_purchase' | 'self_purchase' | 'third_party_purchase' | 'unknown';
type OperatingMetricFilter = 'receivable_rent' | 'received_rent' | 'pending_rent' | 'estimated_max_loss';

const formatCurrency = (value: number) =>
  `¥${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPercent = (value: number) =>
  `${(value || 0).toFixed(2)}%`;

const renderTitleWithTip = (title: string, tip: string) => (
  <span className="operating-title-with-tip">
    <span>{title}</span>
    <Tooltip title={tip}>
      <ExclamationCircleOutlined className="operating-title-tip-icon" />
    </Tooltip>
  </span>
);

const renderJumpValue = (content: React.ReactNode, onClick: () => void) => (
  <span
    className="operating-jump-value"
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    <span className="operating-jump-value-content">{content}</span>
  </span>
);

const APPROVED_SHIPPED_FILTER_REMARK = '';
const DEVICE_COST_FILTER_REMARK = '';

const grossProfitTooltip = (record: OperatingDataStoreRow, mode: 'buyout' | 'return') => {
  const totalCost = (record.deviceCostTotal || 0) + (record.otherCostTotal || 0);
  const receivableRent = record.receivableRent || 0;
  const assetValue = record.buyoutPrice || 0;
  const result = mode === 'buyout' ? (record.estimatedGrossProfitBuyout || 0) : (record.estimatedGrossProfitReturn || 0);
  const formula = mode === 'buyout'
    ? '预估毛利 = 应收租金 + 租完购买价 - 总成本'
    : '预估毛利 = 应收租金 + 固定资产 - 总成本（当前口径固定资产=租完购买价）';

  return (
    <div className="operating-formula-tooltip">
      <div className="operating-formula-title">{formula}</div>
      <div>应收租金：{formatCurrency(receivableRent)}</div>
      <div>{mode === 'buyout' ? '租完购买价' : '固定资产（当前口径=租完购买价）'}：{formatCurrency(assetValue)}</div>
      <div>总成本：{formatCurrency(totalCost)}</div>
      <div>预估毛利：{formatCurrency(result)}</div>
    </div>
  );
};

const grossMarginTooltip = (record: OperatingDataStoreRow, mode: 'buyout' | 'return') => {
  const receivableRent = record.receivableRent || 0;
  const assetValue = record.buyoutPrice || 0;
  const totalRevenue = receivableRent + assetValue;
  const grossProfit = mode === 'buyout' ? (record.estimatedGrossProfitBuyout || 0) : (record.estimatedGrossProfitReturn || 0);
  const margin = mode === 'buyout' ? (record.estimatedMaxGrossMarginBuyout || 0) : (record.estimatedMaxGrossMarginReturn || 0);
  const formula = mode === 'buyout'
    ? '预估最大毛利率 = 预估毛利（买断） ÷ (应收租金 + 租完购买价) × 100%'
    : '预估最大毛利率 = 预估毛利（归还） ÷ (应收租金 + 固定资产) × 100%（当前口径固定资产=租完购买价）';

  return (
    <div className="operating-formula-tooltip">
      <div className="operating-formula-title">{formula}</div>
      <div>{mode === 'buyout' ? '预估毛利（买断）' : '预估毛利（归还）'}：{formatCurrency(grossProfit)}</div>
      <div>应收租金：{formatCurrency(receivableRent)}</div>
      <div>{mode === 'buyout' ? '租完购买价' : '固定资产（当前口径=租完购买价）'}：{formatCurrency(assetValue)}</div>
      <div>分母（应收租金 + 资产项）：{formatCurrency(totalRevenue)}</div>
      <div>预估最大毛利率：{formatPercent(margin)}</div>
    </div>
  );
};

const resolveStoreName = (record: OperatingDataStoreRow, shopMap: any) =>
  record.storeName || shopMap.get?.(Number(record.storeId))?.name || String(record.storeId);

const storeRank = (storeName: string): number => {
  const normalized = String(storeName || '').trim().toUpperCase().replace(/\s+/g, '');
  const numberMatch = normalized.match(/^([1-6])(?:店|号店|点)?$/);
  if (numberMatch) {
    return Number(numberMatch[1]);
  }
  const letterMatch = normalized.match(/^([A-D])(?:店|号店|点)?$/);
  if (letterMatch) {
    return 6 + (letterMatch[1].charCodeAt(0) - 'A'.charCodeAt(0) + 1);
  }
  return Number.MAX_SAFE_INTEGER;
};

const sortStoreRows = (rows: OperatingDataStoreRow[], shopMap: any): OperatingDataStoreRow[] => {
  return [...rows].sort((a, b) => {
    const aName = resolveStoreName(a, shopMap);
    const bName = resolveStoreName(b, shopMap);
    const aRank = storeRank(aName);
    const bRank = storeRank(bName);
    if (aRank !== bRank) {
      return aRank - bRank;
    }
    return String(aName).localeCompare(String(bName), 'zh-Hans-CN');
  });
};

const OperatingDataOverview: React.FC = () => {
  const defaultStartDate = dayjs('2024-12-01');
  const defaultEndDate = dayjs();
  const { shopList, shopMap } = useShopMap();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [latestQuery, setLatestQuery] = useState<any>(undefined);
  const tableRef = useRef<DataTableRef>(null);

  const resolveQueryContext = () => {
    const filter = latestQuery?.filter || {};
    const dateRange = filter.dateRange?.value;
    return {
      startDate: dateRange?.[0] || defaultStartDate.format('YYYY-MM-DD'),
      endDate: dateRange?.[1] || defaultEndDate.format('YYYY-MM-DD'),
      storeId: filter.storeId?.value,
    };
  };

  const jumpToOrderOverview = (
    withApprovedStatuses: boolean,
    rowStoreId?: number,
    warehouseSources?: WarehouseSourceFilter[],
    operatingMetric?: OperatingMetricFilter,
  ) => {
    const { startDate, endDate, storeId } = resolveQueryContext();
    const targetStoreId = rowStoreId ?? storeId;
    const filter: Record<string, any> = {
      order_created_at: {
        operator: 'between',
        value: [startDate, endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      aftersale_status: {
        operator: 'ne',
        value: '已退款',
      },
    };
    if (targetStoreId !== undefined && targetStoreId !== null) {
      filter.store_id = {
        operator: 'in',
        value: [targetStoreId],
      };
    }
    if (withApprovedStatuses) {
      filter.rrzu_status = {
        operator: 'in',
        value: [3, 4, 5, 19, 20],
      };
    }
    if (warehouseSources && warehouseSources.length > 0) {
      filter.warehouse_source = {
        operator: 'in',
        value: warehouseSources,
      };
    }
    if (operatingMetric) {
      filter.operating_metric = {
        operator: 'eq',
        value: operatingMetric,
      };
      filter.operating_start_date = {
        operator: 'eq',
        value: startDate,
      };
      filter.operating_end_date = {
        operator: 'eq',
        value: endDate,
      };
      if (targetStoreId !== undefined && targetStoreId !== null) {
        filter.operating_store_id = {
          operator: 'eq',
          value: targetStoreId,
        };
      }
    }

    const url = `/order/list?q=${encodeURIComponent(JSON.stringify({ filter }))}`;
    window.open(url, '_blank');
  };

  const jumpToRRZFinancialLedger = (rowStoreId?: number) => {
    const { startDate, endDate, storeId } = resolveQueryContext();
    const targetStoreId = rowStoreId ?? storeId;
    const filter: Record<string, any> = {
      cashflowCommissionGeneratedAt: {
        operator: 'between',
        value: [startDate, endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      body: {
        operator: 'regex',
        value: '线下支付倒扣商家|平台分账|退回分账',
      },
    };
    if (targetStoreId !== undefined && targetStoreId !== null) {
      filter.storeId = {
        operator: 'eq',
        value: targetStoreId,
      };
    }
    window.open(`/financial/rrz-financial-ledger?q=${encodeURIComponent(JSON.stringify({ filter }))}`, '_blank');
  };

  const defaultQuery = useMemo(() => ({
    filter: {
      dateRange: {
        operator: 'between',
        value: [defaultStartDate.format('YYYY-MM-DD'), defaultEndDate.format('YYYY-MM-DD')],
      },
    },
  }), [defaultStartDate, defaultEndDate]);

  const queryFields: FieldConfig[] = useMemo(() => [
    {
      key: 'dateRange',
      label: '接单日期',
      type: 'date',
      operators: ['between'],
      format: 'YYYY-MM-DD',
      placeholder: ['开始日期', '结束日期'],
      presets: [
        { label: '近12个月', value: [dayjs().subtract(12, 'month'), dayjs()] },
        { label: '近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
      ],
    },
    {
      key: 'storeId',
      label: '店铺',
      type: 'select',
      operators: ['eq'],
      options: shopList.map((shop) => ({
        label: shop.name || '',
        value: Number(shop.code),
      })),
    },
  ], [shopList]);

  // KPI 卡片配置（6个）
  const buildKPIs = (data: OperatingDataOverviewResponse) => [
    {
      title: '进件订单数',
      value: data.incomingOrderCount || 0,
      color: '#1890ff',
    },
    {
      title: '过审发货订单数',
      value: data.approvedShippedOrderCount || 0,
      color: '#52c41a',
      remark: APPROVED_SHIPPED_FILTER_REMARK,
      remarkColor: '#8c8c8c',
    },
    {
      title: '设备采购成本',
      value: data.deviceCostTotal || 0,
      prefix: '¥',
      precision: 2,
      color: '#faad14',
      remark: DEVICE_COST_FILTER_REMARK,
      remarkColor: '#8c8c8c',
    },
    {
      title: '应收租金',
      value: data.receivableRent || 0,
      prefix: '¥',
      precision: 2,
      color: '#1890ff',
    },
    {
      title: '实收租金',
      value: data.receivedRent || 0,
      prefix: '¥',
      precision: 2,
      color: '#52c41a',
    },
    {
      title: '待收租金',
      value: data.pendingRent || 0,
      prefix: '¥',
      precision: 2,
      color: PENDING_RENT_COLOR,
    },
  ];

  // 表格列配置
  const columns: ColumnsType<OperatingDataStoreRow> = [
    {
      title: '店铺名',
      dataIndex: 'storeId',
      key: 'storeId',
      fixed: 'left',
      width: 50,
      render: (_: number, record: OperatingDataStoreRow) => (
        <Text>{resolveStoreName(record, shopMap)}</Text>
      ),
    },
    {
      title: '进件订单数',
      dataIndex: 'incomingOrderCount',
      key: 'incomingOrderCount',
      align: 'center',
      width: 60,
      render: (value: number, record: OperatingDataStoreRow) => (
        renderJumpValue(value || 0, () => jumpToOrderOverview(false, record.storeId))
      ),
    },
    {
      title: renderTitleWithTip('过审发货订单数', APPROVED_SHIPPED_FILTER_REMARK),
      dataIndex: 'approvedShippedOrderCount',
      key: 'approvedShippedOrderCount',
      align: 'center',
      width: 72,
      render: (value: number, record: OperatingDataStoreRow) => (
        renderJumpValue(
          value || 0,
          () => jumpToOrderOverview(true, record.storeId, ['self_purchase', 'third_party_purchase', 'platform_purchase']),
        )
      ),
    },
    // 设备采购成本（分组）
    {
      title: renderTitleWithTip('合计', DEVICE_COST_FILTER_REMARK),
      dataIndex: 'deviceCostTotal',
      key: 'deviceCostTotal',
      align: 'right',
      width: 110,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(
          formatCurrency(v),
          () => jumpToOrderOverview(true, record.storeId, ['self_purchase', 'third_party_purchase', 'platform_purchase']),
        )
      ),
    },
    {
      title: '公司自采',
      dataIndex: 'companySelfPurchase',
      key: 'companySelfPurchase',
      align: 'right',
      width: 110,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(formatCurrency(v), () => jumpToOrderOverview(true, record.storeId, ['self_purchase', 'third_party_purchase']))
      ),
    },
    {
      title: '平台采购',
      dataIndex: 'platformSelfPurchase',
      key: 'platformSelfPurchase',
      align: 'right',
      width: 110,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(formatCurrency(v), () => jumpToOrderOverview(true, record.storeId, ['platform_purchase']))
      ),
    },
    // 其他成本（分组）
    {
      title: '合计',
      dataIndex: 'otherCostTotal',
      key: 'otherCostTotal',
      align: 'right',
      width: 100,
      render: (v: number) => formatCurrency(v),
    },
    {
      title: '佣金',
      dataIndex: 'commission',
      key: 'commission',
      align: 'right',
      width: 100,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(formatCurrency(v), () => jumpToRRZFinancialLedger(record.storeId))
      ),
    },
    {
      title: '配件费',
      dataIndex: 'accessoryCost',
      key: 'accessoryCost',
      align: 'right',
      width: 100,
      render: (v: number) => formatCurrency(v),
    },
    {
      title: '风控费',
      dataIndex: 'riskControlFee',
      key: 'riskControlFee',
      align: 'right',
      width: 100,
      render: (v: number) => formatCurrency(v),
    },
    {
      title: (
        <span>
          发货费
          <br />
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
            邮费为月结
          </Text>
        </span>
      ),
      dataIndex: 'shippingFee',
      key: 'shippingFee',
      align: 'right',
      width: 100,
      render: (v: number) => formatCurrency(v),
    },
    // 独立列
    {
      title: renderTitleWithTip('库存残值', '库存残值取值来源于现金流数据中的财务人工录入，不再按订单口径自动计算。'),
      dataIndex: 'inventoryResidualValue',
      key: 'inventoryResidualValue',
      align: 'right',
      width: 110,
      render: (v: number) => formatCurrency(v),
    },
    {
      title: renderTitleWithTip('应收租金', '已租订单中的订单总租金和'),
      dataIndex: 'receivableRent',
      key: 'receivableRent',
      align: 'right',
      width: 110,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(formatCurrency(v), () => jumpToOrderOverview(true, record.storeId, undefined, 'receivable_rent'))
      ),
    },
    {
      title: renderTitleWithTip('已收租金', '账单中标记已支付的租金总和，与人人组统计的总实收租金有差异，总实收租金表示已经结算给商家的金额总和'),
      dataIndex: 'receivedRent',
      key: 'receivedRent',
      align: 'right',
      width: 110,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(formatCurrency(v), () => jumpToOrderOverview(true, record.storeId, undefined, 'received_rent'))
      ),
    },
    {
      title: renderTitleWithTip('待收租金', '已逾期、坏账的账单金额之和'),
      dataIndex: 'pendingRent',
      key: 'pendingRent',
      align: 'right',
      width: 110,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(
          <Text style={{ color: v !== 0 ? PENDING_RENT_COLOR : undefined }}>{formatCurrency(v)}</Text>,
          () => jumpToOrderOverview(true, record.storeId, undefined, 'pending_rent'),
        )
      ),
    },
    {
      title: renderTitleWithTip('预估最大资损', '预估资损金额=商品押金-已付租金-已冻结押金'),
      dataIndex: 'estimatedMaxLoss',
      key: 'estimatedMaxLoss',
      align: 'right',
      width: 120,
      render: (v: number, record: OperatingDataStoreRow) => (
        renderJumpValue(
          <Text style={{ color: Math.abs(v || 0) > 0 ? LOSS_COLOR : undefined }}>{formatCurrency(Math.abs(v || 0))}</Text>,
          () => jumpToOrderOverview(true, record.storeId, undefined, 'estimated_max_loss'),
        )
      ),
    },
    // 预计收益 - 买断（分组）
    {
      title: (
        <span>
          预估毛利
          <br />
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
            应收租金 + 租完购买价 - 总成本
          </Text>
        </span>
      ),
      dataIndex: 'estimatedGrossProfitBuyout',
      key: 'estimatedGrossProfitBuyout',
      align: 'right',
      width: 170,
      render: (v: number, record: OperatingDataStoreRow) => (
        <Tooltip title={grossProfitTooltip(record, 'buyout')}>
          <Text style={{ color: EXPECTED_REVENUE_COLOR }}>{formatCurrency(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: (
        <span>
          预估最大毛利率
          <br />
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
            预估毛利 ÷ (应收租金 + 租完购买价)
          </Text>
        </span>
      ),
      dataIndex: 'estimatedMaxGrossMarginBuyout',
      key: 'estimatedMaxGrossMarginBuyout',
      align: 'right',
      width: 190,
      render: (v: number, record: OperatingDataStoreRow) => (
        <Tooltip title={grossMarginTooltip(record, 'buyout')}>
          <Text style={{ color: EXPECTED_REVENUE_COLOR }}>{formatPercent(v)}</Text>
        </Tooltip>
      ),
    },
    // 预计收益 - 归还（分组）
    {
      title: (
        <span>
          预估毛利
          <br />
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
            应收租金 + 固定资产 - 总成本
          </Text>
        </span>
      ),
      dataIndex: 'estimatedGrossProfitReturn',
      key: 'estimatedGrossProfitReturn',
      align: 'right',
      width: 170,
      render: (v: number, record: OperatingDataStoreRow) => (
        <Tooltip title={grossProfitTooltip(record, 'return')}>
          <Text style={{ color: EXPECTED_REVENUE_COLOR }}>{formatCurrency(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: (
        <span>
          预估最大毛利率
          <br />
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
            预估毛利 ÷ (应收租金 + 固定资产)
          </Text>
        </span>
      ),
      dataIndex: 'estimatedMaxGrossMarginReturn',
      key: 'estimatedMaxGrossMarginReturn',
      align: 'right',
      width: 190,
      render: (v: number, record: OperatingDataStoreRow) => (
        <Tooltip title={grossMarginTooltip(record, 'return')}>
          <Text style={{ color: EXPECTED_REVENUE_COLOR }}>{formatPercent(v)}</Text>
        </Tooltip>
      ),
    },
  ];

  // 处理手动添加数据
  const handleAddManualData = async () => {
    try {
      const values = await form.validateFields();
      const dateRange = values.dateRange;
      const params: ManualDataRequest = {
        startDate: dayjs(dateRange[0]).format('YYYY-MM-DD'),
        endDate: dayjs(dateRange[1]).format('YYYY-MM-DD'),
        storeId: values.storeId,
        dataType: values.dataType,
        amount: values.amount,
        remark: values.remark,
      };

      await operatingDataApi.addManualData(params);
      message.success('添加成功');
      setIsModalVisible(false);
      form.resetFields();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('添加失败:', error);
      message.error('添加失败');
    }
  };

  const handleImportSelfPurchaseCost = async (file: File) => {
    setImporting(true);
    try {
      const response = await operatingDataApi.importSelfPurchaseCost(file);
      const result = (response.data || {}) as ImportSelfPurchaseCostResult;
      message.success(
        `导入完成：有效${result.validRows || 0}条，命中自发${result.matchedSelfPurchaseRows || 0}条，新增${result.insertedRows || 0}条，` +
        `更新${result.updatedRows || 0}条，跳过已有${result.skippedExisting || 0}条，` +
        `无订单数据跳过${result.skippedNoOrderData || 0}条，非自发跳过${result.skippedNonSelfPurchase || 0}条，` +
        `无效${result.invalidRows || 0}条，文件内重复${result.duplicateInFile || 0}条`
      );
      if ((result.warehouseSourceConflict || 0) > 0) {
        const conflictOrderNos = (result.warehouseSourceConflictIds || []).slice(0, 20);
        Modal.warning({
          width: 720,
              title: `发现${result.warehouseSourceConflict}笔采购渠道冲突`,
              content: (
                <div>
                  <div>已按表格中的“自发”完成导入，但以下订单与订单表中的货源不一致，请核对：</div>
                  <div style={{ marginTop: 12, maxHeight: 240, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {conflictOrderNos.join('、')}
                    {(result.warehouseSourceConflictIds || []).length > conflictOrderNos.length ? ' 等更多订单' : ''}
              </div>
            </div>
          ),
        });
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('导入自采设备成本失败:', error);
      message.error('导入失败');
    } finally {
      setImporting(false);
    }
  };

  const importUploadProps: UploadProps = {
    accept: '.xlsx,.xls',
    showUploadList: false,
    maxCount: 1,
    beforeUpload: async (file) => {
      await handleImportSelfPurchaseCost(file as File);
      return Upload.LIST_IGNORE;
    },
  };

  return (
    <>
      <Dashboard
        queryFields={queryFields}
        defaultQuery={defaultQuery}
        fetcher={async (params) => {
          setLatestQuery(params);
          const filter = params.filter || {};
          const dateRange = filter.dateRange?.value;

          const requestParams = {
            startDate: dateRange?.[0] || defaultStartDate.format('YYYY-MM-DD'),
            endDate: dateRange?.[1] || defaultEndDate.format('YYYY-MM-DD'),
            storeId: filter.storeId?.value,
          };

          const response = await operatingDataApi.getOverview(requestParams);
          return { rows: response.data.rows || [], stats: response.data };
        }}
        key={refreshTrigger}
        kpiItems={(stats) => buildKPIs((stats || {}) as OperatingDataOverviewResponse)}
        sections={[
          {
            key: 'operating-data-table',
            title: '经营数据分析',
            extra: (
              <Space>
                <Upload {...importUploadProps}>
                  <Button
                    icon={<FileExcelOutlined />}
                    loading={importing}
                  >
                    导入自采设备成本
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                >
                  新增数据
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={() => tableRef.current?.exportCSV({ filename: 'operating-data-overview.csv' })}
                >
                  导出Excel
                </Button>
              </Space>
            ),
            render: ({ rows }) => {
              const sortedRows = sortStoreRows((rows || []) as OperatingDataStoreRow[], shopMap);
              return (
                <DataTable
                  className="operating-data-overview-table"
                  columns={columns}
                  dataSource={sortedRows}
                  rowKey={(r: OperatingDataStoreRow) => String(r.storeId)}
                  groupHeaders={[
                    {
                      title: '设备采购成本',
                      children: ['deviceCostTotal', 'companySelfPurchase', 'platformSelfPurchase'],
                    },
                    {
                      title: '其他成本',
                      children: ['otherCostTotal', 'commission', 'accessoryCost', 'riskControlFee', 'shippingFee'],
                    },
                    {
                      title: '预计收益',
                      children: [
                        {
                          title: '买断',
                          children: ['estimatedGrossProfitBuyout', 'estimatedMaxGrossMarginBuyout'],
                        },
                        {
                          title: '归还',
                          children: ['estimatedGrossProfitReturn', 'estimatedMaxGrossMarginReturn'],
                        },
                      ],
                    },
                  ]}
                  scroll={{ x: 2400 }}
                  ref={tableRef as any}
                />
              );
            },
          },
        ]}
      />

      <Modal
        title="新增数据"
        open={isModalVisible}
        onOk={handleAddManualData}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="dateRange"
            label="日期范围"
            rules={[{ required: true, message: '请选择日期范围' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="storeId" label="店铺">
            <Select
              placeholder="选择店铺（可选）"
              allowClear
              options={shopList.map((shop) => ({
                label: shop.name,
                value: Number(shop.code),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="dataType"
            label="数据类型"
            rules={[{ required: true, message: '请选择数据类型' }]}
          >
            <Select
              placeholder="选择数据类型"
              options={[
                { label: '公司自采（设备采购成本）', value: 'company_self_purchase' },
                { label: '风控费', value: 'risk_control_fee' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="¥"
              precision={2}
              min={0}
              placeholder="请输入金额"
            />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={4} placeholder="请输入备注（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default OperatingDataOverview;
